<template>
  <div class="structure-item" :class="structureItemClasses">
    <!-- Section -->
    <template v-if="item.type === 'title'">
      <div class="section-item d-flex align-center py-4" :class="[sectionClasses, sectionIndentClass]">
        <span class="section-title font-weight-medium text-grey-900 d-flex align-center">
          {{ item.numbering }} {{ item.title }}
        </span>
        <v-chip
          v-if="taskCount > 0"
          size="small"
          class="task-count-chip ml-2"
          density="comfortable"
        >
          {{ $t('project.checklist.dialogs.SmartImportStructureItem.taskCount', { count: taskCount }) }}
        </v-chip>
      </div>

      <!-- Render child tasks -->
      <div v-if="hasChildren" class="children-container d-flex flex-column">
        <SmartImportStructureItem
          v-for="child in item.children"
          :key="child.id"
          :item="child"
          :all-expanded="allExpanded"
        />
      </div>
    </template>

    <!-- Task -->
    <template v-else>
      <div class="task-item px-3" :class="taskIndentClass">
        <div class="d-flex align-start column-gap-2 py-3">
          <div
            v-if="hasDescription"
            class="expand-icon-wrapper flex-shrink-0 d-flex align-center"
            @click="toggleExpanded"
          >
            <app-icon
              size="small"
              icon-weight="fas"
              :icon-name="isExpanded ? 'caret-down' : 'caret-right'"
              class="expand-icon"
            />
          </div>
          <div v-else class="expand-icon-placeholder flex-shrink-0"></div>

          <span class="task-title flex-1 text-grey-700 font-weight-regular">{{ item.numbering }} {{ item.title }}</span>
        </div>

        <!-- Task description -->
        <div v-if="hasDescription && isExpanded" class="task-description px-5 py-4 ml-5">
          <p class="text--small mb-0">{{ item.description }}</p>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, computed, watch } from 'vue'

interface StructureItem {
  id: string
  title: string
  type: 'title' | 'task'
  children?: StructureItem[]
  numbering: string
  description?: string
  depth?: number
}

export default defineComponent({
  name: 'SmartImportStructureItem',
  props: {
    item: {
      type: Object as PropType<StructureItem>,
      required: true,
    },
    allExpanded: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const isExpanded = ref(false)
    const hasChildren = computed(() => props.item.children && props.item.children.length > 0)
    const hasDescription = computed(() => !!props.item.description && props.item.description.trim().length > 0)
    const itemDepth = computed(() => (
      typeof props.item.depth === 'number' && Number.isFinite(props.item.depth)
        ? props.item.depth
        : 0
    ))
    const isTopLevelSection = computed(() => props.item.type === 'title' && itemDepth.value === 0)
    const isNestedSection = computed(() => props.item.type === 'title' && itemDepth.value > 0)

    const taskCount = computed(() => {
      if (props.item.type !== 'title' || !props.item.children) return 0
      return props.item.children.length
    })

    const structureItemClasses = computed(() => ({
      'structure-item--top-level-section': isTopLevelSection.value,
      'structure-item--nested-section': isNestedSection.value,
    }))

    const sectionClasses = computed(() => ({
      'section-item--top-level': isTopLevelSection.value,
      'section-item--nested': isNestedSection.value,
    }))
    const getSpacingClass = (value: number, prefix: string): string => {
      const spacingStep = Math.max(0, Math.min(value * 4, 16))
      return `${prefix}-${spacingStep}`
    }

    const sectionIndentClass = computed(() => getSpacingClass(itemDepth.value, 'pl'))
    const taskIndentClass = computed(() => getSpacingClass(Math.max(itemDepth.value - 1, 0), 'ml'))

    const toggleExpanded = () => {
      isExpanded.value = !isExpanded.value
    }

    // Watch allExpanded prop and update local state
    watch(() => props.allExpanded, (newValue) => {
      if (hasDescription.value) {
        isExpanded.value = newValue
      }
    })

    return {
      isExpanded,
      hasChildren,
      hasDescription,
      taskCount,
      structureItemClasses,
      sectionClasses,
      sectionIndentClass,
      taskIndentClass,
      toggleExpanded,
    }
  },
})
</script>

<style lang="scss" scoped>
.structure-item {
  .section-item {
    flex: 1;
    border-bottom: 1px solid #e7e8ec;

    .section-title {
      min-width: 0;
      word-break: break-word;
      font-size: 18px;
      font-weight: 500;
      color: rgb(var(--v-theme-grey-900));
      line-height: 1.45;
    }

    .task-count-chip {
      font-size: 12px;
      color: rgb(var(--v-theme-grey-700));
      border-radius: 4px;
      padding: 0 4px;
      font-weight: 600;
    }
  }

  &.structure-item--top-level-section {
    .section-item {
      padding-top: 18px;
      padding-bottom: 18px;
    }
  }

  &.structure-item--nested-section {
    .section-item {
      .section-title {
        font-size: 14px;
        font-weight: 500;
      }
    }
  }

  .children-container {
    // Add margin-top to first child only if it's a task (not a section)
    > .structure-item:first-child:has(> .task-item) {
      margin-top: 12px;
    }
  }

  .task-item {
    .task-title {
      min-width: 0;
      word-break: break-word;
      line-height: 1.4;
      font-size: 14px;
      font-weight: 400;
      color: rgb(var(--v-theme-grey-700));
    }

    .expand-icon-wrapper {
      cursor: pointer;
      padding-top: 2px;
    }

    .expand-icon {
      pointer-events: none;
      color: rgb(var(--v-theme-grey-600));
    }

    .expand-icon-placeholder {
      width: 16px;
    }

    .task-description {
      background-color: rgb(var(--v-theme-grey-25));
      border-radius: 8px;
      color: rgb(var(--v-theme-grey-700));
      line-height: 20px;
      border: 1px solid rgb(var(--v-theme-grey-100));
    }
  }
}
</style>
